import { FC, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { findAll } from 'highlight-words-core'
import en from 'src/constants/en'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/arrow-down-s-line.svg'
import { ReactComponent as ExternalLinkSvg } from 'src/assets/svg/external-link.svg'
import { ReactComponent as PlanetIcon } from 'src/assets/svg/planet.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { DEFAULT_ORIGINAL_LANG_CODE } from 'src/constants/defaultOriginalLangCode'
import { PLATFORM_LABEL } from 'src/pages/ReviewAnalysis/constants'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { ReviewListItem } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './reviewCard.module.scss'

// Evaluation buckets mirror the filter: 5 → Excellent, 3-4 → Good, 1-2 → Poor.
const evaluation = (rating: number): { className: string; label: string } => {
  if (rating >= 5)
    return { className: styles.badgeExcellent, label: 'Excellent' }
  if (rating >= 3) return { className: styles.badgeGood, label: 'Good' }
  return { className: styles.badgePoor, label: 'Poor' }
}

// "ru" → "Russian". Falls back to the bare code for anything Intl can't name.
const languageLabel = (code: string): string => {
  try {
    return (
      new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ??
      code.toUpperCase()
    )
  } catch {
    return code.toUpperCase()
  }
}

// The review text with every match of the applied search marked.
const Highlighted: FC<{ term?: string; text: string }> = ({ term, text }) => {
  const word = term?.trim()
  if (!word) return <>{text}</>
  const chunks = findAll({
    autoEscape: true,
    caseSensitive: false,
    searchWords: [word],
    textToHighlight: text,
  })
  return (
    <>
      {chunks.map(({ end, highlight, start }) => {
        const part = text.substring(start, end)
        return highlight ? (
          <mark className={styles.highlight} key={start}>
            {part}
          </mark>
        ) : (
          <span key={start}>{part}</span>
        )
      })}
    </>
  )
}

interface ReviewCardProps {
  checked?: boolean
  // The applied search term, marked in the text.
  highlight?: string
  // Omitted where nothing is selectable (the topic modal's Related Reviews):
  // no checkbox, no hover shift.
  onToggleSelect?: (id: string) => void
  review: ReviewListItem
  // The subtopic the review was tagged with (the topic modal's reviews).
  subtopic?: string | null
}

export const ReviewCard: FC<ReviewCardProps> = ({
  checked = false,
  highlight,
  onToggleSelect,
  review,
  subtopic,
}) => {
  const selectable = !!onToggleSelect
  const [expanded, setExpanded] = useState(false)
  const [showTranslation, setShowTranslation] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)
  const PlatformIcon = getPlatformIcon(review.platform)
  const { label, className } = evaluation(review.rating)
  const rounded = Math.round(review.rating)

  // Translations are STORED by the ops pipeline (never fetched here): the
  // toggle just swaps between the two texts, exactly like social listening's
  // mention rows. No `translated` → no toggle.
  const original = review.text?.trim() ?? ''
  const translation = review.translated?.trim() ?? ''
  const hasTranslation = !!original && !!translation && translation !== original
  const text = hasTranslation && showTranslation ? translation : review.text
  const language =
    review.language && review.language !== DEFAULT_ORIGINAL_LANG_CODE
      ? languageLabel(review.language)
      : null

  // Only offer the expand arrow when the (1-line clamped) text actually
  // overflows; short reviews that fit on one line stay non-expandable. Measured
  // against the clamped layout, so skip while expanded (nothing is clamped).
  useLayoutEffect(() => {
    if (expanded) return
    const el = textRef.current
    if (el) setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
  }, [text, expanded])

  return (
    // Only the checkbox selects, as in Inbox and Social Listening: clicking
    // the card body (to read, expand, or open the review) must not tick it.
    // The checkbox is revealed on hover/selection via CSS.
    <div
      className={classNames(
        styles.card,
        checked && styles.checked,
        !selectable && styles.static
      )}
    >
      {selectable && (
        <div className={styles.checkboxWrapper}>
          <input
            checked={checked}
            className={styles.checkbox}
            onChange={() => onToggleSelect?.(review._id)}
            type="checkbox"
          />
        </div>
      )}
      <div className={styles.topRow}>
        <div className={styles.source}>
          <span className={styles.platformIcon}>
            <PlatformIcon />
          </span>
          <span className={styles.platformName}>
            {PLATFORM_LABEL[review.platform] ?? review.platform}
          </span>
          {!!review.venueName && (
            <>
              <span className={styles.separator}>-</span>
              <span className={styles.venueName}>{review.venueName}</span>
            </>
          )}
          {review.url && (
            <a
              className={styles.externalLink}
              href={review.url}
              onClick={(e) => e.stopPropagation()}
              rel="noreferrer"
              target="_blank"
            >
              <ExternalLinkSvg />
            </a>
          )}
        </div>

        <div className={styles.ratingBlock}>
          <span className={styles.ratingValue}>
            {Number(review.rating || 0).toFixed(1)}
          </span>
          <span className={styles.stars}>
            {[0, 1, 2, 3, 4].map((i) => (
              <StarIcon
                className={classNames(
                  styles.star,
                  i < rounded ? styles.starFilled : styles.starEmpty
                )}
                key={i}
              />
            ))}
          </span>
          <span className={classNames(styles.badge, className)}>{label}</span>
        </div>
      </div>

      <div className={styles.meta}>
        <span className={styles.time}>{moment(review.date).fromNow()}</span>
        {review.reviewerName && (
          <>
            <span className={styles.metaDot}>·</span>
            <span className={styles.reviewer}>{review.reviewerName}</span>
          </>
        )}
        {language && (
          <span className={styles.languageLabel}>
            <PlanetIcon />
            {language}
          </span>
        )}
        {!!subtopic && (
          <span className={styles.subtopicChip} title="Subtopic">
            {subtopic}
          </span>
        )}
      </div>

      {!original ? (
        // Rating-only review: no body text to expand.
        <span className={styles.noText}>{en.noReviewText}</span>
      ) : isOverflowing ? (
        <button
          className={styles.textRow}
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((v) => !v)
          }}
          type="button"
        >
          <span
            className={classNames(styles.text, !expanded && styles.textClamped)}
            ref={textRef}
          >
            <Highlighted term={highlight} text={text ?? ''} />
          </span>
          <ChevronIcon
            className={classNames(styles.chevron, expanded && styles.chevronUp)}
          />
        </button>
      ) : (
        // Short review that fits on one line — no expand control.
        <span
          className={classNames(styles.text, styles.textClamped)}
          ref={textRef}
        >
          <Highlighted term={highlight} text={text ?? ''} />
        </span>
      )}

      {hasTranslation && (
        <div className={styles.translations}>
          <button
            className={styles.translate}
            onClick={(e) => {
              e.stopPropagation()
              setShowTranslation((v) => !v)
            }}
            type="button"
          >
            {showTranslation ? en.seeOriginal : en.seeTranslation}
          </button>
        </div>
      )}
    </div>
  )
}
